import Multiselect, { MultiselectOption } from '@/app/components/Multiselect'
import { Icon, Text } from '@/components'
import { Props as IconProps } from '@/components/Icon/Icon'
import { ComponentProps } from 'react'
import { EnergySystem } from '@/types/energySystem'
import { VehicleCharger, Thermostat, Vehicle } from '@/types'
import { UtilityProgram } from '@/types/utilityProgram'
import {
  unenrollmentEnergySystemDisplayName,
  unenrollmentChargerDisplayName,
  unenrollmentThermostatDisplayName,
  unenrollmentVehicleDisplayName,
} from '../../utils'
import {
  BatteryReasons,
  ChargerReasons,
  ThermostatReasons,
  VehicleReasons,
  ProgramReasons,
} from '../constants'

// Base component for all device unenrollment reason sections
export type Props = {
  className?: string
  deviceNames: string[]
  iconName: IconProps['name']
  reasons: MultiselectOption[]
  onChange: (values: MultiselectOption[]) => void
}

export default function DeviceUnenrollmentReasonSection(props: Props) {
  return (
    <div className={props.className}>
      <div className="flex flex-row items-start mb-4">
        <div className="min-w-[20px] h-[20px]">
          <Icon name={props.iconName} size={20} color="base-grey-900" />
        </div>
        <Text variant="body1" className="!leading-[143%] ml-2 h-fit">
          Reasons for unenrolling {props.deviceNames.join(' & ')}:
        </Text>
      </div>
      <Multiselect
        options={props.reasons}
        onChange={props.onChange}
        className="flex-col gap-4"
      />
    </div>
  )
}

// Base props for all device-specific components
export type BaseDeviceUnenrollmentProps = {
  onChange: ComponentProps<typeof DeviceUnenrollmentReasonSection>['onChange']
}

// Device-specific component types using intersection
export type BatteryUnenrollmentReasonSectionProps =
  BaseDeviceUnenrollmentProps & {
    energySystems: EnergySystem[]
  }

export type ChargerUnenrollmentReasonSectionProps =
  BaseDeviceUnenrollmentProps & {
    chargers: VehicleCharger[]
  }

export type ThermostatUnenrollmentReasonSectionProps =
  BaseDeviceUnenrollmentProps & {
    thermostats: Thermostat[]
  }

export type VehicleUnenrollmentReasonSectionProps =
  BaseDeviceUnenrollmentProps & {
    vehicles: Vehicle[]
  }

export type ProgramUnenrollmentReasonSectionProps =
  BaseDeviceUnenrollmentProps & {
    utilityProgram: UtilityProgram
    incentivesExist: boolean
  }

// Device-specific components
export function BatteryUnenrollmentReasonSection(
  props: BatteryUnenrollmentReasonSectionProps,
) {
  if (props.energySystems.length === 0) {
    return null
  }

  return (
    <DeviceUnenrollmentReasonSection
      deviceNames={props.energySystems.map((e) =>
        unenrollmentEnergySystemDisplayName(e),
      )}
      iconName="BatteryCharging"
      reasons={BatteryReasons}
      onChange={props.onChange}
    />
  )
}

export function ChargerUnenrollmentReasonSection(
  props: ChargerUnenrollmentReasonSectionProps,
) {
  if (props.chargers.length === 0) {
    return null
  }

  return (
    <DeviceUnenrollmentReasonSection
      deviceNames={props.chargers.map((c) => unenrollmentChargerDisplayName(c))}
      iconName="Zap"
      reasons={ChargerReasons}
      onChange={props.onChange}
    />
  )
}

export function ThermostatUnenrollmentReasonSection(
  props: ThermostatUnenrollmentReasonSectionProps,
) {
  if (props.thermostats.length === 0) {
    return null
  }

  return (
    <DeviceUnenrollmentReasonSection
      deviceNames={props.thermostats.map((t) =>
        unenrollmentThermostatDisplayName(t),
      )}
      iconName="Thermometer"
      reasons={ThermostatReasons}
      onChange={props.onChange}
    />
  )
}

export function VehicleUnenrollmentReasonSection(
  props: VehicleUnenrollmentReasonSectionProps,
) {
  if (props.vehicles.length === 0) {
    return null
  }

  return (
    <DeviceUnenrollmentReasonSection
      deviceNames={props.vehicles.map((vehicle) =>
        unenrollmentVehicleDisplayName(vehicle),
      )}
      iconName="PlainVehicle"
      reasons={VehicleReasons}
      onChange={props.onChange}
    />
  )
}

export function ProgramUnenrollmentReasonSection(
  props: ProgramUnenrollmentReasonSectionProps,
) {
  const reasons = ProgramReasons.filter(
    // if incentives don't exists for this program, we don't want to
    // show the reason related to rewards
    (r) => props.incentivesExist || !r.requiresIncentivesOnProgram,
  )

  return (
    <DeviceUnenrollmentReasonSection
      deviceNames={[`from ${props.utilityProgram.name}`]}
      iconName="Settings"
      reasons={reasons}
      onChange={props.onChange}
    />
  )
}
